import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { WorkflowEtape, WorkflowTransition } from '../models/models';
import api from './api';

interface WorkflowEtapeState {
  workflowEtapeData: WorkflowEtape[];
  transitions: WorkflowTransition[];
  total: number;
  totalPages: number;
  currentPage: number;
  loading: boolean;
  error: string | null;
  successMessage: string | null;
  selectedWorkflowEtape: WorkflowEtape | null;
}

const initialState: WorkflowEtapeState = {
  workflowEtapeData: [],
  transitions: [],
  total: 0,
  totalPages: 1,
  currentPage: 1,
  loading: false,
  error: null,
  successMessage: null,
  selectedWorkflowEtape: null,
};

export const fetchWorkflowEtape = createAsyncThunk(
  'workflowEtape/fetchAll',
  async (params: {
    workflow_id?: number | null;
    source_code?: string | null;
    limit: number;
    offset: number;
  }, { rejectWithValue }) => {
    try {
      const response = await api.get('/workflowEtape', { params });
      return response.data;
    } catch (err: any) {
      return rejectWithValue(err.response?.data?.message ?? 'Erreur chargement workflow');
    }
  }
);

export const fetchWorkflowTransitions = createAsyncThunk(
  'workflowEtape/fetchTransitions',
  async (params: { source_code: string; departement_id?: number | null }, { rejectWithValue }) => {
    try {
      const response = await api.get('/workflowEtape/transitions', { params });
      return response.data as WorkflowTransition[];
    } catch (err: any) {
      return rejectWithValue(err.response?.data?.message ?? 'Erreur chargement transitions');
    }
  }
);

export const saveWorkflowEtape = createAsyncThunk(
  'workflowEtape/save',
  async (etape: WorkflowEtape, { rejectWithValue }) => {
    try {
      const response = await api.post('/workflowEtape', etape);
      return response.data;
    } catch (err: any) {
      return rejectWithValue(err.response?.data?.message ?? 'Erreur enregistrement étape');
    }
  }
);

export const deleteWorkflowEtape = createAsyncThunk(
  'workflowEtape/delete',
  async (id: number, { rejectWithValue }) => {
    try {
      await api.delete('/workflowEtape', { params: { id } });
      return id;
    } catch (err: any) {
      return rejectWithValue(err.response?.data?.message ?? 'Erreur suppression étape');
    }
  }
);

const WorkflowEtapeSlice = createSlice({
  name: 'WorkflowEtapeSlice',
  initialState,
  reducers: {
    setSelectedWorkflowEtape(state, action: PayloadAction<WorkflowEtape | null>) {
      state.selectedWorkflowEtape = action.payload;
    },
    clearWorkflowEtapeData(state) {
      state.workflowEtapeData = [];
      state.transitions = [];
      state.total = 0;
      state.totalPages = 1;
      state.currentPage = 1;
    },
    clearMessages(state) {
      state.error = null;
      state.successMessage = null;
    },
  },
  extraReducers: (builder) => {
    // fetchWorkflowEtape
    builder
      .addCase(fetchWorkflowEtape.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchWorkflowEtape.fulfilled, (state, action) => {
        state.loading = false;
        state.workflowEtapeData = action.payload.data;
        state.total       = action.payload.total;
        state.totalPages  = action.payload.totalPages;
        state.currentPage = action.payload.currentPage;
      })
      .addCase(fetchWorkflowEtape.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });

    // fetchWorkflowTransitions
    builder
      .addCase(fetchWorkflowTransitions.fulfilled, (state, action) => {
        state.transitions = action.payload;
      });

    // saveWorkflowEtape
    builder
      .addCase(saveWorkflowEtape.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(saveWorkflowEtape.fulfilled, (state) => {
        state.loading = false;
        state.successMessage = 'Étape enregistrée avec succès';
        state.selectedWorkflowEtape = null;
      })
      .addCase(saveWorkflowEtape.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });

    // deleteWorkflowEtape
    builder
      .addCase(deleteWorkflowEtape.fulfilled, (state, action) => {
        state.successMessage = 'Étape supprimée';
        state.workflowEtapeData = state.workflowEtapeData.filter(
          (e) => e.id !== action.payload
        );
      })
      .addCase(deleteWorkflowEtape.rejected, (state, action) => {
        state.error = action.payload as string;
      });
  },
});

export const {
  setSelectedWorkflowEtape,
  clearWorkflowEtapeData,
  clearMessages,
} = WorkflowEtapeSlice.actions;

export default WorkflowEtapeSlice.reducer;
